import Autocomplete from '@mui/material/Autocomplete';
import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useDebounce } from '../../hooks';
import { NBSP, strIsNullOrEmpty } from '../../utils';
import { LinkButton } from '../button/link-button';
import { MuiMenuItem } from '../mui-list';
import { TextField } from '../text-field';
import { AddressInputForm } from './address-input-form';
import { AddressSearchOption, AddressStateType, IAddressInputField } from './types';
import { addressStateToString, buildDefaultEditAddressState, formatGeocodeResult, geocodeAsync, shouldAttemptGeocode } from './utils';
import { useI18n } from 'fox-extensions';

async function getAddressSuggestionsAsync<T>(
	address: string,
	region: string,
	convertSuggestion: (r: google.maps.places.AutocompletePrediction) => T
): Promise<T[]> {
	const geocoder = new google.maps.places.AutocompleteService();
	return new Promise<T[]>((resolve, reject) => {
		try {
			geocoder.getPlacePredictions(
				{
					componentRestrictions: {
						country: region
					},
					input: address
				},
				(res, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK) resolve(res?.map(r => convertSuggestion(r)) ?? []);
					else resolve([]);
				}
			);
		} catch (e) {
			reject(e);
		}
	});
}

export function useDebouncedAddressSearch<T>(
	valueToSearch: string,
	delay: number,
	convertSuggestion: (r: google.maps.places.AutocompletePrediction) => T
) {
	const i18n = useI18n();
	const state = useState<T[]>([]);
	const debouncedAddress = useDebounce(valueToSearch, delay);
	const [isFetching, setIsFetching] = useState<boolean>(false);
	React.useEffect(() => {
		async function querySuggestions() {
			if (!strIsNullOrEmpty(debouncedAddress)) {
				setIsFetching(true);
				getAddressSuggestionsAsync(debouncedAddress, i18n.countryCode, convertSuggestion)
					.then(list => {
						state[1](list);
						setIsFetching(false);
					})
					.catch(() => setIsFetching(false));
			}
		}
		querySuggestions();
	}, [debouncedAddress]);
	return [state[0], state[1], isFetching] as const;
}

/** Has the option to expand into individual fields */

/** this is to prevent inconsistent padding behaviour */
export const InputFormWrapper = styled.div<{ fullSize?: boolean }>`
	${props => (!props.fullSize ? "* input {box-sizing: inherit;'}" : '')}
`;

export const CustomAddressInputField: React.FC<IAddressInputField> = props => {
	/** arbitrary string used as value for the custom address option in the drop down */
	const CUSTOM_ADDRESS_VALUE = 'This-is-a-custom-address';

	/**whether to show the expanded form with all the fields */
	const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

	/**The address the user has input to search for */
	const [search, setSearch] = React.useState('');
	const i18n = useI18n();

	/**The suggestions from google */
	const [results, setResults] = useDebouncedAddressSearch<AddressStateType>(search, 750, r => {
		return { formatted: r.description };
	});

	const [inputValue, setInputValue] = useState('');

	const [editAddressState, setEditAddressState] = useState<AddressStateType | null>(null);

	function expand() {
		const newState = props.value ?? (strIsNullOrEmpty(search) ? buildDefaultEditAddressState(i18n.countryCode) : { ...buildDefaultEditAddressState(i18n.countryCode), address1: search });
		newState.formatted = addressStateToString(newState);
		setEditAddressState(newState);
		setIsExpanded(true);
	}
	function collapse() {
		onFormChange(editAddressState);
		setIsExpanded(false);
		// reset search
		setSearch('');
		setResults([]);
		setInputValue('');
	}

	const onFormChange = (editAddressState: AddressStateType | null) => {
		if (editAddressState) {
			props?.onChange?.({ ...editAddressState });
		}
		// If editAddressState is null, do not call onChange (or handle as needed)
	};

	// ensure the selected value is in the all results list
	if (props.value) {
		const v = props.value?.formatted;
		if (v && !strIsNullOrEmpty(props.value?.address1) && !results.some(a => a.formatted === v)) {
			results.push(props.value);
		}
	}

	const options = [
		...(results ?? [])
			.concat(editAddressState?.formatted && editAddressState?.address1 ? editAddressState : [])
			.filter((value, index, self) => self.findIndex(o => o.formatted === value.formatted) === index)
			.map(r => {
				return { key: r.formatted, value: JSON.stringify(r), label: r.formatted ?? '' };
			})
	];

	const value = options.find(o => o.label === props.value?.formatted && !strIsNullOrEmpty(props.value?.address1)) ?? null;
	return (
		<>
			{isExpanded ? (
				<div style={props.fullSize ? { width: '100%' } : {}}>
					<InputFormWrapper fullSize={props.fullSize}>
						<AddressInputForm
							addressState={editAddressState}
							variant={props.fullSize ? 'labelled' : 'placeholders'}
							size={props.size}
							fieldProps={{
								variant: 'outlined',
								className: 'addressInput',
								size: props.size,
								style: { boxSizing: 'inherit' }
							}}
							onFieldBlur={newState => {
								if (shouldAttemptGeocode(newState)) {
									const wtf = addressStateToString(newState);
									//console.log("Attempting to geocode with: ", wtf, newState);
									geocodeAsync(wtf, i18n.countryCode)
										.then(({ res }) => {
											const gr = formatGeocodeResult(res?.[0]);
											//console.log("New result", gr);
											newState.lat = gr?.lat;
											newState.lng = gr?.lng;
											setEditAddressState(newState);
											onFormChange(newState);
										})
										.catch(e => {
											console.warn('Geocode attempt fail', e);
											setEditAddressState(newState);
											onFormChange(newState);
										});
								}
							}}
							onChange={newState => {
								setEditAddressState(newState);
								onFormChange(newState);
							}}
						/>
					</InputFormWrapper>
					<div className="link-button form-to-search">
						<LinkButton onClick={collapse}>
							<div className="link-button form-to-search">{'< Go back to address lookup'}</div>
						</LinkButton>
					</div>
				</div>
			) : (
				<div style={props.width ? { width: props.width } : {}}>
					<Autocomplete<AddressSearchOption, false, false>
						className="addressInput"
						renderInput={params => (
							<TextField
								{...params}
								className="addressInput"
								InputProps={{
									...params.InputProps,
									endAdornment: <></>,
									type: 'search'
								}}
								size={props.size ?? 'small'}
								label={props.label}
								helperText={props.helperText}
								error={props.error}
							/>
						)}
						onChange={(_, newVal: AddressSearchOption | null) => {
							if (newVal) {
								if (newVal.value === CUSTOM_ADDRESS_VALUE) {
									expand();
									return;
								}
								const parsed = JSON.parse(newVal.value);
								geocodeAsync(newVal.label, i18n.countryCode)
									.then(({ res }) => {
										const formattedRes = formatGeocodeResult(res?.[0]);
										onFormChange(formattedRes);
										setInputValue(parsed.formatted);
										setResults([]);
									})
									.catch(e => console.log('Error for geocoding result in Address Search', e));
							} else {
								onFormChange(null);
								setInputValue('');
							}
						}}
						onInputChange={(_, val) => {
							setSearch(val);
							setInputValue(val);
							// reset selected address
							if (strIsNullOrEmpty(val)) {
								onFormChange(null);
								setResults([]);
							}
						}}
						inputValue={inputValue}
						value={value}
						options={options.concat({
							key: 'custom',
							value: CUSTOM_ADDRESS_VALUE,
							label: results?.length ? 'None of the above?' : 'Enter Manually'
						})}
						// this is needed to update options rendering properly
						filterOptions={x => x}
						renderOption={(props, option) => {
							return (
								<MuiMenuItem {...props} key={option?.key} value={option?.value}>
									{option?.label}
								</MuiMenuItem>
							);
						}}
					></Autocomplete>
					<div className="link-button search-to-form">
						Or{NBSP}
						<LinkButton onClick={expand}>
							<div style={props.colour ? { color: props.colour } : {}} className="link-button search-to-form">
								Enter address manually...
							</div>
						</LinkButton>
					</div>
				</div>
			)}
		</>
	);
};
